import { API_CALL, SET_TOUR_V2_SELECTED_PACKAGE_TYPE } from '../actionConstants'
import {
  FETCH_TAILOR_MADE_TOUR,
  FETCH_TAILOR_MADE_TOUR_ORDER,
  FETCH_TOURV2_OFFER,
  FETCH_TOURV2_SNAPSHOT_DETAILS, SEND_WAITLIST_REQUEST,
  FETCH_TOUR_DEALS,
  SET_TOUR_PRICE_MODE,
  FETCH_TOUR_DEAL,
  FETCH_TOUR_DEAL_DETAILS,
  FETCH_TOUR_DEAL_FACETS,
} from '../apiActionConstants'
import {
  getSnapshotDetailsForTourV2Offer,
  requestGetTailorMadeTour,
  requestGetTailorMadeTourOrder,
  sendWaitingListRequestForTourV2,
  getTourDeals,
  getTourDeal,
  getTourDealDetails,
  getTourDealFacets,
} from 'api/tour/tourV2'
import * as OfferService from 'api/offer'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { AppAction } from '../types/ActionTypes'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getTourV2OfferCacheKey } from 'lib/tours/tourV2OfferCacheKey'

export { getTourV2OfferCacheKey } from 'lib/tours/tourV2OfferCacheKey'

export function fetchTourV2Offer(
  offerId: string,
  privateRequestKey?: string,
  regionCode?: string,
  allPackages?: boolean,
  retryFailed?: boolean,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    const offerKey = getTourV2OfferCacheKey(offerId, privateRequestKey, allPackages)

    if (state.offer.offersLoading[offerKey] ||
      state.offer.tourV2Offers[offerKey] ||
      (!retryFailed && state.offer.offerErrors[offerKey])) {
      return
    }
    dispatch({
      type: API_CALL,
      api: FETCH_TOURV2_OFFER,
      request: () =>
        OfferService.getOfferById(offerId, {
          region: regionCode || state.geo.currentRegionCode,
          privateRequestKey,
          allPackages,
        }) as Promise<App.Tours.TourV2Offer>,
      offerId: offerKey,
    })
  }
}

interface FetchTourV2SnapshotDetailsRouteParams {
  orderId: string
}

export function fetchTourV2SnapshotDetails(
  reservationId: string,
  routeParams: FetchTourV2SnapshotDetailsRouteParams,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    if (state.order.tourV2SnapshotsLoading[reservationId] ||
      state.order.tourV2SnapshotDetails[reservationId]) {
      return
    }
    dispatch({
      type: API_CALL,
      api: FETCH_TOURV2_SNAPSHOT_DETAILS,
      request: () =>
        getSnapshotDetailsForTourV2Offer({ ...routeParams, reservationId }),
      reservationId,
    })
  }
}

export function sendWaitListTourSoldOut(requestWaitingListTourData: App.Tours.WaitingListTourRequest) {
  return {
    type: API_CALL,
    api: SEND_WAITLIST_REQUEST,
    request: () => {
      const intl = getCurrentIntl()
      return sendWaitingListRequestForTourV2(requestWaitingListTourData)
        .then(() => {
          showSnackbar(
            intl.formatMessage({
              id: 'SrstCl5D1zD3sC-q8t0y',
              defaultMessage: 'We will keep you notified if a spot on this tour becomes available.',
              description: 'Snackbar message shown after successfully joining a tour waitlist',
            }),
            'success',
            {
              heading: intl.formatMessage({
                id: 'oX-gcFbh_TdGj0FA9yE7',
                defaultMessage: 'You’ve joined the waitlist',
                description: 'Snackbar heading shown after successfully joining a tour waitlist',
              }),
            },
          )
        })
        .catch(() => {
          showSnackbar(
            intl.formatMessage({
              id: '7GUo6UqKs1D6F9wplpIq',
              defaultMessage: 'Please try again later or contact us for assistance.',
              description: 'Snackbar body message when an unexpected tour action error occurs',
            }),
            'critical',
            {
              heading: intl.formatMessage({
                id: '6ELk9pAa-_dhTJjsdPH8',
                defaultMessage: 'Oops! Something went wrong',
                description: 'Snackbar heading when an unexpected tour action error occurs',
              }),
            },
          )
        })
    },
  }
}

export function fetchTailorMadeTour(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.tour.tailorMadeTour?.data || state.tour.tailorMadeTour?.fetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TAILOR_MADE_TOUR,
      request: () => {
        return requestGetTailorMadeTour({ region: state.geo.currentRegionCode })
      },
    })
  }
}

export function fetchTailorMadeTourOrder(tailorMadeTourRequestId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const order = state.tour.tailorMadeTourOrders[tailorMadeTourRequestId]

    if (order?.data || order?.fetching) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TAILOR_MADE_TOUR_ORDER,
      tailorMadeTourRequestId,
      request: () => {
        return requestGetTailorMadeTourOrder(tailorMadeTourRequestId, { region: state.geo.currentRegionCode })
      },
    })
  }
}

export function fetchTourDeals(region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDeals, fetchingTourDeals } = state.tour

    if ((tourDeals && Object.values(tourDeals).length)
      || fetchingTourDeals) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEALS,
      request: () => getTourDeals(region),
    })
  }
}

export function fetchTourDeal(slug: string, region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDeals, fetchingTourDealBySlugAndFacets } = state.tour

    if (tourDeals?.[slug] || fetchingTourDealBySlugAndFacets?.[slug]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEAL,
      slug,
      request: () => {
        return getTourDeal(slug, region)
      },
    })
  }
}

export function fetchTourDealFacets(slug: string, region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDealFacets, fetchingTourDealFacetsBySlug } = state.tour

    if (tourDealFacets?.[slug] || fetchingTourDealFacetsBySlug?.[slug]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEAL_FACETS,
      slug,
      request: () => {
        // Fetch the initial facets for the tour deal
        // by getting the tour deal details with no filters
        return getTourDealFacets(slug, region)
      },
    })
  }
}

export function fetchTourDealDetails(slug: string, uniqueKey: string, filters: App.Tours.TourDealFiltersApplied, sortParams: App.Tours.TourDealSortState, region: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { tourDealsDetails, fetchingTourDealDetailsBySlug } = state.tour

    if (tourDealsDetails?.[uniqueKey] || fetchingTourDealDetailsBySlug?.[uniqueKey]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_TOUR_DEAL_DETAILS,
      slug: uniqueKey,
      request: () => {
        return getTourDealDetails(slug, filters, sortParams, region)
      },
    })
  }
}

export function setTourPriceMode(tourPriceMode: 'total' | 'perDay'): AppAction {
  return {
    type: SET_TOUR_PRICE_MODE,
    tourPriceMode,
  }
}

export function setTourV2SelectedPackageType(offerId: string, packageType: string | undefined): AppAction {
  return {
    type: SET_TOUR_V2_SELECTED_PACKAGE_TYPE,
    offerId,
    packageType,
  }
}
